import React, { useState, useEffect } from 'react';
import { Timeline, Spin, Empty, Typography } from 'antd';
import axios from 'axios';

const { Text } = Typography;

// Create a custom axios instance without auth interceptors for public endpoints
const publicAxios = axios.create({
  baseURL: '', // Use same base URL
  timeout: 10000,
});

// Clear any existing interceptors and prevent inheritance from global axios
publicAxios.interceptors.request.handlers = [];
publicAxios.interceptors.response.handlers = [];

// Override the default headers to ensure no auth headers are sent
publicAxios.defaults.headers.common = {};
publicAxios.defaults.headers.get = {};
publicAxios.defaults.headers.post = {};
publicAxios.defaults.headers.put = {};
publicAxios.defaults.headers.patch = {};
publicAxios.defaults.headers.delete = {};

// Add a request interceptor to ensure no Authorization header is sent
publicAxios.interceptors.request.use(
  (config) => {
    // Remove any Authorization header that might have been added
    delete config.headers.Authorization;
    delete config.headers.authorization;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor that doesn't handle 401s like the global one
publicAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't try to refresh tokens or redirect on 401 for public endpoints
    return Promise.reject(error);
  }
);

const ExpressTrackingTimeline = ({ 
  expressNumber, 
  recipientPhone = "0000", 
  onTrackingUpdate, 
  showOverseasOnly = false, 
  orderId,
  trajectoryText,
  trajectoryUpdateAt,
  isDelivered = false,
  deliveryTrajectory = null,
  orderStatus = null
}) => {
  const [sfLoading, setSfLoading] = useState(false);
  const [sfTrackingData, setSfTrackingData] = useState([]);
  const [sfError, setSfError] = useState(null);
  
  const [overseasLoading, setOverseasLoading] = useState(false);
  const [overseasTrackingData, setOverseasTrackingData] = useState([]);
  const [overseasError, setOverseasError] = useState(null);
  
  const [dbTrackingData, setDbTrackingData] = useState(null);
  const [dbTrackingTime, setDbTrackingTime] = useState(null);
  const [showingDbData, setShowingDbData] = useState(false);

  // Use trajectory data passed from parent component
  useEffect(() => {
    if (trajectoryText && trajectoryUpdateAt) {
      try {
        const parsedData = typeof trajectoryText === 'string' 
          ? JSON.parse(trajectoryText) 
          : trajectoryText;
        
        // Using trajectory data from props
        setDbTrackingData(parsedData);
        setDbTrackingTime(new Date(trajectoryUpdateAt));
        setShowingDbData(true);
        
        // Use the DB data while we fetch fresh data
        const formattedDbData = parsedData.map(item => ({
          ...item,
          accept_time: item.acceptTime || item.accept_time,
          remark: item.remark,
          accept_address: item.acceptAddress || item.accept_address
        }));
        setSfTrackingData(formattedDbData);
      } catch (error) {
        console.error('Error parsing trajectory data from props:', error);
      }
    } else {
      // No trajectory data provided in props
    }
  }, [trajectoryText, trajectoryUpdateAt]);

  // For public tracking, we don't need to fetch data again as it's already provided by the backend
  useEffect(() => {
    // If we have sf_tracking_data from the backend, use it
    if (orderStatus?.sf_tracking_data) {
      const formattedData = orderStatus.sf_tracking_data.map(item => ({
        ...item,
        accept_time: item.acceptTime || item.accept_time,
        remark: item.remark,
        accept_address: item.acceptAddress || item.accept_address
      }));
      setSfTrackingData(formattedData);
    }
    
    // If we have overseas_tracking_data from the backend, use it
    if (orderStatus?.overseas_tracking_data) {
      setOverseasTrackingData(orderStatus.overseas_tracking_data);
    }
  }, [orderStatus]);


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div>
      {showingDbData && dbTrackingTime && (
        <div style={{ marginBottom: '10px', padding: '0 12px' }}>
          <Text type="secondary">
            显示数据库记录 (更新于: {formatDateTime(dbTrackingTime)})
          </Text>
        </div>
      )}
      <Timeline style={{ padding: '12px' }}>
      {/* Overseas tracking data */}
      {overseasLoading ? (
        <Timeline.Item>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="small" /> <span style={{ marginLeft: '8px' }}>加载海外轨迹中...</span>
          </div>
        </Timeline.Item>
      ) : overseasTrackingData && overseasTrackingData.length > 0 && (
        <>
          {overseasTrackingData
            .filter(item => 
              item.remark === '起飞' || 
              item.remark === '实际到港' || 
              item.remark === '清关放行'
            )
            .map((item, index) => (
              <Timeline.Item key={`overseas-${index}`} color="blue">
                <>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                  {formatDate(item.accept_time)}
                </div>
                <div>{`${item.remark === '实际到港' ? '清关中' : item.remark === '起飞' ? '国际运输中' : '已放行'}`}</div>
              </>
              </Timeline.Item>
            ))}
        </>
      )}

      {/* SF tracking data */}
      {sfTrackingData && sfTrackingData.length > 0 && (
        <>
          {showOverseasOnly ? (
            // For public tracking, only show delivery confirmation if available
            sfTrackingData.some(item => item.remark.includes('签收') || item.remark.includes('送达')) && !isDelivered && (
              <Timeline.Item>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                  {formatDate(sfTrackingData[sfTrackingData.length - 1].accept_time)}
                </div>
                <div>快件已签收</div>
              </Timeline.Item>
            )
          ) : (
            // For internal tracking, show all SF tracking data
            sfTrackingData.map((item, index) => (
              <Timeline.Item key={index}>
                <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
                  {formatDateTime(item.accept_time)}
                </div>
                <div>{item.remark}</div>
                {item.accept_address && (
                  <div style={{ color: '#666', fontSize: '12px' }}>
                    {item.accept_address}
                  </div>
                )}
              </Timeline.Item>
            ))
          )}
        </>
      )}
      
      {/* Add 已送达 timeline item for delivered orders */}
      {isDelivered && deliveryTrajectory && showOverseasOnly && (
        <Timeline.Item color="green">
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>
            {formatDate(deliveryTrajectory.acceptTime || deliveryTrajectory.accept_time || '')}
          </div>
          <div>已送达</div>
        </Timeline.Item>
      )}
      
      {/* Show loading indicator without blocking the data */}
      {!showOverseasOnly && sfLoading && (
        <Timeline.Item>
          <div style={{ textAlign: 'center', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '10px' }}>
            <Spin size="small" /> <span style={{ marginLeft: '8px' }}>正在获取最新轨迹...</span>
          </div>
        </Timeline.Item>
      )}
      </Timeline>
    </div>
  );
};

export default ExpressTrackingTimeline;
